import React, { useState } from 'react';
import { Upload, X, Image, FileText, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

const CustomerLabelUpload = ({ 
  onImageUploaded, 
  currentImage,
  className = "",
  skipUpload = false // New prop to skip server upload (for Builder)
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const { toast } = useToast();
  const { token, isAuthenticated } = useAuth();

  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
  const maxSize = 10 * 1024 * 1024; // 10MB
  const minSize = 10 * 1024; // 10KB minimum (aligned with Area invio file)

  const validateFile = (file) => {
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Per le etichette sono consentiti solo: JPG, PNG, PDF');
    }
    
    if (file.size > maxSize) {
      throw new Error('File troppo grande. Massimo 10MB');
    }

    if (file.size < minSize) {
      throw new Error('File troppo piccolo. Dimensione minima: 10KB');
    }
  };

  const uploadFile = async (file) => {
    setUploading(true);
    
    try {
      validateFile(file);
      
      // If skipUpload is true (Builder mode), upload to temporary storage
      if (skipUpload) {
        const formData = new FormData();
        formData.append('file', file);
        
        const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
        
        const response = await fetch(`${backendUrl}/api/upload/builder/label`, {
          method: 'POST',
          body: formData
        });
        
        const result = await response.json();
        
        if (response.ok && result.success) {
          toast({
            title: "Etichetta caricata",
            description: `${file.name} caricata con successo`,
            variant: "default"
          });
          
          if (onImageUploaded) {
            // Pass relative path (better for database storage) and file object
            onImageUploaded(result.filePath, file);
          }
        } else {
          throw new Error(result.message || 'Errore durante l\'upload');
        }
        
        setUploading(false);
        return;
      }
      
      // Normal server upload flow (for authenticated users in Dashboard)
      const formData = new FormData();
      formData.append('file', file);
      
      // Get backend URL from environment
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      if (!token || !isAuthenticated()) {
        throw new Error('Devi effettuare il login per caricare il tuo logo personalizzato');
      }
      
      const response = await fetch(`${backendUrl}/api/upload/customer/label`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        toast({
          title: "Logo caricato",
          description: `${file.name} caricato con successo`,
          variant: "default"
        });
        
        if (onImageUploaded) {
          // Pass both the file path and the file object for 3D preview generation
          onImageUploaded(result.filePath, file);
        }
      } else {
        throw new Error(result.message || 'Errore durante l\'upload');
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Errore upload",
        description: error.message || 'Errore durante l\'upload del file',
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      uploadFile(file);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    
    const file = event.dataTransfer.files[0];
    if (file) {
      uploadFile(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragActive(false);
  };

  const removeImage = () => {
    if (onImageUploaded) {
      onImageUploaded(null);
    }
  };

  const getFileIcon = (filePath) => {
    if (!filePath) return <Upload className="w-6 h-6 text-gray-400" />;
    
    const extension = filePath.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') {
      return <FileText className="w-6 h-6 text-red-500" />;
    } else {
      return <Image className="w-6 h-6 text-blue-500" />;
    }
  };

  // Show login prompt if user is not authenticated
  if (!isAuthenticated() || !token) {
    return (
      <div className={className}>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center bg-gray-50">
          <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600 mb-2">
            Caricamento etichetta disponibile per utenti registrati
          </p>
          <p className="text-xs text-gray-500">
            <a href="/login" className="text-purple-600 hover:text-purple-700 underline">
              Accedi
            </a> o{' '}
            <a href="/register" className="text-purple-600 hover:text-purple-700 underline">
              registrati
            </a> per caricare la tua etichetta personalizzata
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Current Image Display */}
      {currentImage && (
        <div className="mb-4">
          <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {getFileIcon(currentImage)}
                <div>
                  <p className="text-sm font-medium text-gray-900">Etichetta caricata</p>
                  <p className="text-xs text-gray-500">{currentImage.split('/').pop()}</p>
                </div>
              </div>
              <button
                onClick={removeImage}
                className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                title="Rimuovi etichetta"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Area */}
      <div 
        className={`relative border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
          dragActive 
            ? 'border-purple-400 bg-purple-50' 
            : 'border-gray-300 hover:border-gray-400'
        } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {uploading ? (
          <div className="space-y-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto"></div>
            <p className="text-sm text-gray-600">Caricamento...</p>
          </div>
        ) : (
          <div className="space-y-2">
            <Upload className="w-6 h-6 text-gray-400 mx-auto" />
            <div>
              <p className="text-sm text-gray-900">
                <label className="text-purple-600 hover:text-purple-500 cursor-pointer underline">
                  Carica la tua etichetta
                  <input
                    type="file"
                    className="hidden"
                    accept="image/jpeg,image/jpg,image/png,application/pdf"
                    onChange={handleFileSelect}
                    disabled={uploading}
                  />
                </label>
              </p>
              <p className="text-xs text-gray-500">
                JPG, PNG o PDF • Min 1MB • Max 10MB • Alta risoluzione
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Guidelines */}
      <div className="mt-3 bg-blue-50 p-3 rounded">
        <div className="flex">
          <AlertCircle className="w-4 h-4 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
          <div className="text-xs text-blue-700 space-y-1">
            <p className="font-medium">Requisiti Etichetta:</p>
            <p>• Dimensioni: 9x9 cm</p>
            <p>• Alta risoluzione (min. 1MB)</p>
            <p>• Formati: JPG, PNG, PDF</p>
            <p>• Il file verrà applicato come etichetta sulla bottiglia</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerLabelUpload;